import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  ArrowDownUp,
  Calendar,
  CalendarRange,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Clock3,
  DollarSign,
  Filter,
  GripVertical,
  Paperclip,
  Plus,
  Search,
  User,
} from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { format, isPast, isThisWeek, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Task } from '../../types';
import { Badge, getPriorityVariant, priorityLabels } from '../common/Badge';
import { TaskPeople } from './TaskPeople';

interface TaskTableViewProps {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  onAddTask: (status?: Task['status']) => void;
  onStatusChange?: (taskId: string, status: Task['status']) => void;
}

type ColumnKey =
  | 'title'
  | 'assignee'
  | 'status'
  | 'priority'
  | 'due_date'
  | 'budget'
  | 'files'
  | 'timeline'
  | 'updated_at';

type DueDateFilter = 'all' | 'overdue' | 'today' | 'this_week' | 'no_due';

type SortDirection = 'asc' | 'desc';

type StatusStyle = { bg: string; label: string };

const STATUS_STYLE_STORAGE_KEY = 'crm-agro-task-status-colors';

const MONDAY_STATUS_PALETTE = [
  { name: 'Verde', color: '#00C875' },
  { name: 'Laranja', color: '#FDAB3D' },
  { name: 'Vermelho', color: '#E2445C' },
  { name: 'Cinza', color: '#C4C4C4' },
  { name: 'Azul', color: '#579BFC' },
  { name: 'Roxo', color: '#784BD1' },
  { name: 'Amarelo', color: '#FFCB00' },
  { name: 'Verde escuro', color: '#037F4C' },
  { name: 'Coral', color: '#FF7575' },
  { name: 'Preto', color: '#333333' },
  { name: 'Ciano', color: '#66CCFF' },
  { name: 'Lilás', color: '#A25DDC' },
];

const DEFAULT_STATUS_STYLES: Record<Task['status'], StatusStyle> = {
  todo:        { bg: '#C24914', label: 'Não iniciado' },
  in_progress: { bg: '#FDAB3D', label: 'Em Andamento' },
  in_review:   { bg: '#579BFC', label: 'Em Revisão'   },
  done:        { bg: '#00C875', label: 'Feito'         },
};

const statusGroupLabels: Record<Task['status'], string> = {
  todo: 'Tarefas pendentes',
  in_progress: 'Em Andamento',
  in_review: 'Em Revisão',
  done: 'Concluído',
};

function loadStatusStyles(): Record<Task['status'], StatusStyle> {
  if (typeof window === 'undefined') return DEFAULT_STATUS_STYLES;

  try {
    const stored = window.localStorage.getItem(STATUS_STYLE_STORAGE_KEY);
    if (!stored) return DEFAULT_STATUS_STYLES;
    const colors = JSON.parse(stored) as Partial<Record<Task['status'], string>>;

    return {
      todo: { ...DEFAULT_STATUS_STYLES.todo, bg: colors.todo || DEFAULT_STATUS_STYLES.todo.bg },
      in_progress: { ...DEFAULT_STATUS_STYLES.in_progress, bg: colors.in_progress || DEFAULT_STATUS_STYLES.in_progress.bg },
      in_review: { ...DEFAULT_STATUS_STYLES.in_review, bg: colors.in_review || DEFAULT_STATUS_STYLES.in_review.bg },
      done: { ...DEFAULT_STATUS_STYLES.done, bg: colors.done || DEFAULT_STATUS_STYLES.done.bg },
    };
  } catch {
    return DEFAULT_STATUS_STYLES;
  }
}

function saveStatusColors(styles: Record<Task['status'], StatusStyle>) {
  window.localStorage.setItem(
    STATUS_STYLE_STORAGE_KEY,
    JSON.stringify({
      todo: styles.todo.bg,
      in_progress: styles.in_progress.bg,
      in_review: styles.in_review.bg,
      done: styles.done.bg,
    })
  );
}

const allColumns: { key: ColumnKey; label: string; className: string; icon: React.ReactNode; sortable?: boolean }[] = [
  { key: 'title',      label: 'Tarefa',          className: 'min-w-[260px]',     icon: <CheckSquare size={14} className="text-gray-400" />,  sortable: true  },
  { key: 'assignee',   label: 'Responsável',      className: 'w-44 min-w-[170px]', icon: <User size={14} className="text-gray-400" />,          sortable: true  },
  { key: 'status',     label: 'Status',           className: 'w-40 min-w-[140px]', icon: <Filter size={14} className="text-gray-400" />,        sortable: true  },
  { key: 'priority',   label: 'Prioridade',       className: 'w-32 min-w-[120px]', icon: <ArrowDownUp size={14} className="text-gray-400" />,   sortable: true  },
  { key: 'due_date',   label: 'Prazo',            className: 'w-36 min-w-[140px]', icon: <Calendar size={14} className="text-gray-400" />,      sortable: true  },
  { key: 'budget',     label: 'Orçamento',        className: 'w-32 min-w-[120px]', icon: <DollarSign size={14} className="text-gray-400" />,    sortable: true  },
  { key: 'files',      label: 'Arquivos',         className: 'w-24 min-w-[100px]', icon: <Paperclip size={14} className="text-gray-400" />,     sortable: true  },
  { key: 'timeline',   label: 'Cronograma',       className: 'w-44 min-w-[160px]', icon: <CalendarRange size={14} className="text-gray-400" />, sortable: false },
  { key: 'updated_at', label: 'Última atualiz.',  className: 'w-44 min-w-[170px]', icon: <Clock3 size={14} className="text-gray-400" />,        sortable: true  },
];

function getFileCount(task: Task) {
  return Number(task.attachment_count || 0);
}

function formatBudget(value: number | null | undefined) {
  if (value == null || Number(value) === 0) return null;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(value));
}

function formatTimeline(startDate?: string | null, dueDate?: string | null): string | null {
  const start = startDate ? new Date(startDate) : null;
  const end   = dueDate   ? new Date(dueDate)   : null;
  if (!start && !end) return null;
  if (start && end) return `${format(start, 'd MMM', { locale: ptBR })} - ${format(end, 'd MMM', { locale: ptBR })}`;
  if (end)   return format(end, 'd MMM', { locale: ptBR });
  return format(start!, 'd MMM', { locale: ptBR });
}

function getTaskValue(task: Task, field: ColumnKey): string | number {
  switch (field) {
    case 'title':     return task.title.toLowerCase();
    case 'assignee':  return (task.assignee_name || '').toLowerCase();
    case 'status':    return task.status;
    case 'priority': {
      const order: Record<Task['priority'], number> = { low: 0, medium: 1, high: 2, critical: 3 };
      return order[task.priority] ?? 0;
    }
    case 'due_date':   return task.due_date ? new Date(task.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    case 'budget':     return Number(task.budget || 0);
    case 'files':      return getFileCount(task);
    case 'timeline':   return task.due_date ? new Date(task.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    case 'updated_at': return new Date(task.updated_at).getTime();
    default:           return '';
  }
}

// ─── StatusDropdown ───────────────────────────────────────────────────────────

function StatusDropdown({
  status,
  statusStyles,
  onChange,
  onColorChange,
}: {
  status: Task['status'];
  statusStyles: Record<Task['status'], StatusStyle>;
  onChange?: (s: Task['status']) => void;
  onColorChange: (status: Task['status'], color: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const style = statusStyles[status];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={e => { e.stopPropagation(); if (onChange) setOpen(o => !o); }}
        className="text-xs font-semibold px-3 py-1.5 rounded-sm transition-opacity hover:opacity-80 w-[110px] text-center text-white"
        style={{ backgroundColor: style.bg }}
      >
        {style.label}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden min-w-[210px]">
          <div className="py-1">
            {(Object.entries(statusStyles) as [Task['status'], StatusStyle][]).map(([key, s]) => (
              <button
                key={key}
                type="button"
                onClick={e => { e.stopPropagation(); onChange?.(key); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-gray-50 text-left"
              >
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: s.bg }} />
                <span className="text-gray-700">{s.label}</span>
              </button>
            ))}
          </div>

          <div className="border-t border-gray-100 p-3">
            <p className="text-[11px] font-medium text-gray-500 mb-2">Cor deste status</p>
            <div className="grid grid-cols-6 gap-1.5">
              {MONDAY_STATUS_PALETTE.map(item => (
                <button
                  key={item.color}
                  type="button"
                  title={item.name}
                  aria-label={`Usar cor ${item.name}`}
                  onClick={e => {
                    e.stopPropagation();
                    onColorChange(status, item.color);
                  }}
                  className={`w-6 h-6 rounded-sm border transition-transform hover:scale-110 ${
                    item.color === style.bg ? 'border-gray-900 ring-2 ring-gray-300' : 'border-white'
                  }`}
                  style={{ backgroundColor: item.color }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function TaskTableView({ tasks, onTaskClick, onAddTask, onStatusChange }: TaskTableViewProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Task['status']>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | Task['priority']>('all');
  const [dueDateFilter, setDueDateFilter] = useState<DueDateFilter>('all');
  const [sortField, setSortField] = useState<ColumnKey>('updated_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(
    new Set(allColumns.map(column => column.key))
  );
  const [columnOrder, setColumnOrder] = useState<ColumnKey[]>(
    allColumns.map(column => column.key)
  );
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const [statusStyles, setStatusStyles] = useState<Record<Task['status'], StatusStyle>>(loadStatusStyles);

  const statusGroups = useMemo(
    () => (Object.keys(statusGroupLabels) as Task['status'][]).map(key => ({
      key,
      label: statusGroupLabels[key],
      color: statusStyles[key].bg,
    })),
    [statusStyles]
  );

  const toggleGroup = (status: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const assigneeOptions = useMemo(() => {
    const names = new Set(tasks.flatMap(task => [
      task.assignee_name?.trim(),
      ...(task.participants?.map(participant => participant.name.trim()) || []),
    ]).filter(Boolean) as string[]);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  const filteredAndSortedTasks = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    const filtered = tasks.filter(task => {
      if (statusFilter !== 'all' && task.status !== statusFilter) return false;
      if (
        assigneeFilter !== 'all'
        && task.assignee_name !== assigneeFilter
        && !task.participants?.some(participant => participant.name === assigneeFilter)
      ) return false;
      if (priorityFilter !== 'all' && task.priority !== priorityFilter) return false;

      if (dueDateFilter !== 'all') {
        const dueDate = task.due_date ? new Date(task.due_date) : null;

        if (dueDateFilter === 'no_due' && dueDate) return false;
        if (dueDateFilter === 'today' && (!dueDate || !isToday(dueDate))) return false;
        if (dueDateFilter === 'this_week' && (!dueDate || !isThisWeek(dueDate, { weekStartsOn: 1 }))) return false;
        if (
          dueDateFilter === 'overdue' &&
          (!dueDate || !isPast(dueDate) || task.status === 'done')
        ) {
          return false;
        }
      }

      if (!normalizedQuery) return true;
      const searchable = `${task.title} ${task.description || ''} ${task.assignee_name || ''} ${task.participants?.map(participant => participant.name).join(' ') || ''}`.toLowerCase();
      return searchable.includes(normalizedQuery);
    });

    return filtered.sort((left, right) => {
      const leftValue = getTaskValue(left, sortField);
      const rightValue = getTaskValue(right, sortField);

      if (leftValue < rightValue) return sortDirection === 'asc' ? -1 : 1;
      if (leftValue > rightValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tasks, query, statusFilter, assigneeFilter, priorityFilter, dueDateFilter, sortField, sortDirection]);

  const orderedVisibleColumns = columnOrder
    .map(key => allColumns.find(column => column.key === key))
    .filter((column): column is NonNullable<typeof column> => !!column && visibleColumns.has(column.key));

  const selectedColumnsCount = orderedVisibleColumns.length;

  const handleToggleColumn = (columnKey: ColumnKey) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(columnKey)) {
        if (next.size === 1) return prev;
        next.delete(columnKey);
      } else {
        next.add(columnKey);
      }
      return next;
    });
  };

  const handleSort = (column: ColumnKey) => {
    if (sortField === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(column);
    setSortDirection('asc');
  };

  const handleStatusColorChange = (status: Task['status'], color: string) => {
    setStatusStyles(prev => {
      const next = {
        ...prev,
        [status]: { ...prev[status], bg: color },
      };
      saveStatusColors(next);
      return next;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setColumnOrder(prev => {
      const oldIndex = prev.indexOf(active.id as ColumnKey);
      const newIndex = prev.indexOf(over.id as ColumnKey);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const isTableEmpty = filteredAndSortedTasks.length === 0;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between">
          <div className="relative w-full lg:max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Pesquisar tarefas..."
              className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/25 focus:border-primary-500"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={statusFilter}
              onChange={event => setStatusFilter(event.target.value as 'all' | Task['status'])}
              className="h-9 px-3 text-xs border border-gray-200 rounded-lg bg-white text-gray-700"
            >
              <option value="all">Status: Todos</option>
              <option value="todo">A Fazer</option>
              <option value="in_progress">Em Andamento</option>
              <option value="in_review">Em Revisão</option>
              <option value="done">Concluído</option>
            </select>

            <select
              value={assigneeFilter}
              onChange={event => setAssigneeFilter(event.target.value)}
              className="h-9 px-3 text-xs border border-gray-200 rounded-lg bg-white text-gray-700"
            >
              <option value="all">Responsável: Todos</option>
              {assigneeOptions.map(assignee => (
                <option key={assignee} value={assignee}>
                  {assignee}
                </option>
              ))}
            </select>

            <select
              value={priorityFilter}
              onChange={event => setPriorityFilter(event.target.value as 'all' | Task['priority'])}
              className="h-9 px-3 text-xs border border-gray-200 rounded-lg bg-white text-gray-700"
            >
              <option value="all">Prioridade: Todas</option>
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
              <option value="critical">Crítica</option>
            </select>

            <select
              value={dueDateFilter}
              onChange={event => setDueDateFilter(event.target.value as DueDateFilter)}
              className="h-9 px-3 text-xs border border-gray-200 rounded-lg bg-white text-gray-700"
            >
              <option value="all">Prazo: Todos</option>
              <option value="today">Hoje</option>
              <option value="this_week">Esta semana</option>
              <option value="overdue">Atrasados</option>
              <option value="no_due">Sem prazo</option>
            </select>

            <div className="relative">
              <button
                type="button"
                onClick={() => setIsColumnMenuOpen(prev => !prev)}
                className="h-9 px-3 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 hover:bg-gray-50"
              >
                Colunas ({selectedColumnsCount})
              </button>
              {isColumnMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg z-20 p-2">
                  <p className="text-[11px] text-gray-500 px-2 pb-1">Selecionar colunas visíveis</p>
                  <div className="max-h-56 overflow-y-auto">
                    {allColumns.map(column => (
                      <label
                        key={column.key}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          aria-label={column.label}
                          checked={visibleColumns.has(column.key)}
                          onChange={() => handleToggleColumn(column.key)}
                        />
                        <span className="text-xs text-gray-700">{column.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="w-full overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full min-w-[1080px] border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="w-1 px-0" />
                <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                  {orderedVisibleColumns.map(column => (
                    <SortableHeaderCell
                      key={column.key}
                      id={column.key}
                      className={column.className}
                      label={column.label}
                      icon={column.icon}
                      sortable={column.sortable}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={() => handleSort(column.key)}
                    />
                  ))}
                </SortableContext>
              </tr>
            </thead>

            <tbody>
              {statusGroups.map(group => {
                const groupTasks = filteredAndSortedTasks.filter(task => task.status === group.key);
                const isCollapsed = collapsedGroups.has(group.key);

                // Summary calculations
                const totalBudget = groupTasks.reduce((sum, t) => sum + Number(t.budget || 0), 0);
                const totalFiles  = groupTasks.reduce((sum, t) => sum + getFileCount(t), 0);
                const groupStarts = groupTasks.map(t => t.start_date ? new Date(t.start_date).getTime() : null).filter(Boolean) as number[];
                const groupEnds   = groupTasks.map(t => t.due_date   ? new Date(t.due_date).getTime()   : null).filter(Boolean) as number[];
                const groupTimelineLabel = formatTimeline(
                  groupStarts.length ? new Date(Math.min(...groupStarts)).toISOString() : null,
                  groupEnds.length   ? new Date(Math.max(...groupEnds)).toISOString()   : null,
                );

                return (
                  <React.Fragment key={group.key}>
                    {/* ── Group header row ── */}
                    <tr
                      className="cursor-pointer hover:bg-gray-50 border-b border-gray-100"
                      style={{ borderLeft: `4px solid ${group.color}` }}
                      onClick={() => toggleGroup(group.key)}
                    >
                      <td className="px-4 py-2.5" colSpan={selectedColumnsCount + 1}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-gray-400">
                            {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                          </span>
                          <span className="text-sm font-semibold" style={{ color: group.color }}>
                            {group.label}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                            {groupTasks.length}
                          </span>
                        </div>
                      </td>
                    </tr>

                    {!isCollapsed && (
                      <>
                        {groupTasks.length === 0 ? (
                          <tr className="border-b border-gray-100" style={{ borderLeft: `4px solid ${group.color}` }}>
                            <td colSpan={selectedColumnsCount + 1} className="px-12 py-3 text-sm text-gray-400 italic">
                              Nenhuma tarefa neste grupo
                            </td>
                          </tr>
                        ) : (
                          groupTasks.map(task => (
                            <TaskRow
                              key={task.id}
                              task={task}
                              onClick={() => onTaskClick(task)}
                              groupColor={group.color}
                              visibleColumns={orderedVisibleColumns.map(c => c.key)}
                              statusStyles={statusStyles}
                              onStatusChange={onStatusChange}
                              onStatusColorChange={handleStatusColorChange}
                            />
                          ))
                        )}

                        {/* ── Add task row ── */}
                        <tr className="border-b border-gray-100" style={{ borderLeft: `4px solid ${group.color}` }}>
                          <td colSpan={selectedColumnsCount + 1}>
                            <button
                              onClick={() => onAddTask(group.key)}
                              className="w-full flex items-center gap-2 px-12 py-2 text-sm text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors text-left"
                            >
                              <Plus size={14} />
                              Criar tarefa
                            </button>
                          </td>
                        </tr>

                        {/* ── Summary row ── */}
                        <tr className="border-b border-gray-200" style={{ backgroundColor: `${group.color}12`, borderLeft: `4px solid ${group.color}` }}>
                          <td className="w-1 px-0" />
                          {orderedVisibleColumns.map((col, idx) => {
                            if (idx === 0) return <td key={col.key} className="px-3 py-2" />;
                            if (col.key === 'status') {
                              return (
                                <td key={col.key} className="px-3 py-2">
                                  <span className="text-xs font-semibold px-2 py-1 rounded-sm text-white" style={{ backgroundColor: group.color }}>
                                    {group.label}
                                  </span>
                                </td>
                              );
                            }
                            if (col.key === 'due_date') {
                              const dates = groupTasks.map(t => t.due_date).filter(Boolean) as string[];
                              if (!dates.length) return <td key={col.key} className="px-3 py-2" />;
                              const nearest = new Date(Math.min(...dates.map(d => new Date(d).getTime())));
                              return (
                                <td key={col.key} className="px-3 py-2">
                                  <span className="text-xs font-medium bg-gray-900 text-white px-2 py-1 rounded">
                                    {format(nearest, 'd MMM', { locale: ptBR })}
                                  </span>
                                </td>
                              );
                            }
                            if (col.key === 'budget') {
                              return (
                                <td key={col.key} className="px-3 py-2">
                                  <span className="text-xs text-gray-700">
                                    {formatBudget(totalBudget) ?? '$0'}{' '}
                                    <span className="text-gray-400 text-[10px]">Total</span>
                                  </span>
                                </td>
                              );
                            }
                            if (col.key === 'files') {
                              return (
                                <td key={col.key} className="px-3 py-2">
                                  <span className="text-xs text-gray-500">{totalFiles} Arquivos</span>
                                </td>
                              );
                            }
                            if (col.key === 'timeline') {
                              return (
                                <td key={col.key} className="px-3 py-2">
                                  {groupTimelineLabel ? (
                                    <span className="text-xs font-medium bg-gray-900 text-white px-2 py-1 rounded">
                                      {groupTimelineLabel}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-300">—</span>
                                  )}
                                </td>
                              );
                            }
                            return <td key={col.key} className="px-3 py-2" />;
                          })}
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                );
              })}

              {isTableEmpty && (
                <tr>
                  <td className="px-6 py-8 text-center text-sm text-gray-500" colSpan={selectedColumnsCount + 1}>
                    Nenhuma tarefa encontrada com os filtros atuais.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </DndContext>

      {/* ── Add group button ── */}
      <button
        type="button"
        onClick={() => onAddTask()}
        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-dashed border-gray-300 hover:border-primary-300"
      >
        <Plus size={14} />
        Adicionar novo grupo
      </button>
    </div>
  );
}

function SortableHeaderCell({
  id,
  className,
  label,
  icon,
  sortable,
  sortField,
  sortDirection,
  onSort,
}: {
  id: ColumnKey;
  className: string;
  label: string;
  icon: React.ReactNode;
  sortable?: boolean;
  sortField: ColumnKey;
  sortDirection: SortDirection;
  onSort: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <th
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide ${className} ${
        isDragging ? 'bg-primary-100/50' : ''
      }`}
    >
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 text-left ${sortable ? 'hover:text-gray-700' : ''}`}
          onClick={sortable ? onSort : undefined}
        >
          {icon}
          <span>{label}</span>
          {sortable && (
            <ArrowDownUp
              size={13}
              className={sortField === id ? 'text-primary-600' : 'text-gray-300'}
            />
          )}
          {sortable && sortField === id && (
            <span className="text-[10px] text-primary-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </button>
        <button
          type="button"
          className="text-gray-300 hover:text-gray-500"
          aria-label={`Reordenar coluna ${label}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical size={12} />
        </button>
      </div>
    </th>
  );
}

interface TaskRowProps {
  task: Task;
  onClick: () => void;
  groupColor: string;
  visibleColumns: ColumnKey[];
  statusStyles: Record<Task['status'], StatusStyle>;
  onStatusChange?: (taskId: string, status: Task['status']) => void;
  onStatusColorChange: (status: Task['status'], color: string) => void;
}

function TaskRow({
  task,
  onClick,
  groupColor,
  visibleColumns,
  statusStyles,
  onStatusChange,
  onStatusColorChange,
}: TaskRowProps) {
  const isOverdue = task.due_date && isPast(new Date(task.due_date)) && task.status !== 'done';
  const timeline  = formatTimeline(task.start_date, task.due_date);

  return (
    <tr
      className="border-b border-gray-100 hover:bg-primary-50/30 cursor-pointer group transition-colors"
      style={{ borderLeft: `4px solid ${groupColor}` }}
      onClick={onClick}
    >
      <td className="w-1 px-0" />

      {visibleColumns.map(column => {
        if (column === 'title') {
          return (
            <td key={column} className="px-3 py-3 align-middle">
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-gray-900 truncate group-hover:text-primary-700 transition-colors">
                  {task.title}
                </span>
                {task.description && (
                  <span className="text-xs text-gray-400 truncate mt-0.5">{task.description}</span>
                )}
              </div>
            </td>
          );
        }

        if (column === 'assignee') {
          return (
            <td key={column} className="px-3 py-3 align-middle">
              <TaskPeople task={task} showNames />
            </td>
          );
        }

        if (column === 'status') {
          return (
            <td key={column} className="px-3 py-2 align-middle" onClick={e => e.stopPropagation()}>
              <StatusDropdown
                status={task.status}
                statusStyles={statusStyles}
                onColorChange={onStatusColorChange}
                onChange={onStatusChange ? s => onStatusChange(task.id, s) : undefined}
              />
            </td>
          );
        }

        if (column === 'priority') {
          return (
            <td key={column} className="px-3 py-3 align-middle">
              <Badge variant={getPriorityVariant(task.priority)}>
                {priorityLabels[task.priority] || task.priority}
              </Badge>
            </td>
          );
        }

        if (column === 'due_date') {
          return (
            <td key={column} className="px-3 py-3 align-middle">
              {task.due_date ? (
                <span className={`text-xs font-medium ${isOverdue ? 'text-red-600' : 'text-gray-700'}`}>
                  {format(new Date(task.due_date), 'dd MMM yyyy')}
                </span>
              ) : (
                <span className="text-xs text-gray-300">—</span>
              )}
            </td>
          );
        }

        if (column === 'budget') {
          const formatted = formatBudget(task.budget);
          return (
            <td key={column} className="px-3 py-3 align-middle">
              {formatted ? (
                <span className="text-xs text-gray-700">{formatted}</span>
              ) : (
                <span className="text-xs text-gray-300">—</span>
              )}
            </td>
          );
        }

        if (column === 'files') {
          return (
            <td key={column} className="px-3 py-3 align-middle">
              <span className="text-xs text-gray-700">{getFileCount(task)}</span>
            </td>
          );
        }

        if (column === 'timeline') {
          return (
            <td key={column} className="px-3 py-2 align-middle">
              {timeline ? (
                <span className="text-xs font-medium bg-gray-900 text-white px-2 py-1 rounded">
                  {timeline}
                </span>
              ) : (
                <span className="text-xs text-gray-300">—</span>
              )}
            </td>
          );
        }

        // updated_at (default)
        return (
          <td key={column} className="px-3 py-3 align-middle">
            <span className="text-xs text-gray-500">
              {format(new Date(task.updated_at), "dd MMM 'às' HH:mm")}
            </span>
          </td>
        );
      })}
    </tr>
  );
}
